/* ==========================================================================
   Field states - shared by the appointment, contact and style-consult forms.
   Figma: node 5231:2112 ("Form - Success & Error States"), FIELD STATES half.
   Pairs with assets/field-state.css.

   USAGE - mark the form and let it wire itself up:

     <form class="…" data-field-state>                     shell = the input itself
     <form class="…" data-field-state data-fs-shell=".sc__field">   shell = a wrapper

   "Shell" is whichever element carries the border. Per-field overrides:
     data-fs-valid="…"   replace the "Looks good." text
     data-fs-error="…"   replace the invalid text
     data-fs-skip        leave this field alone entirely

   WHEN IT VALIDATES: nothing is marked until a field has been "touched" (blurred
   once) - showing a red border on an empty form the visitor hasn't typed in yet is
   hostile. After the first blur the field re-validates live on every keystroke, so
   an error clears the moment it's fixed. Calling validateAll() (see below) touches
   and validates everything at once, for submit time.

     form.fieldState.validateAll()  -> true if every field is valid

   No dependencies. Safe to load on pages with several forms.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__fieldStateReady) return; // three sections may each load this
  window.__fieldStateReady = true;

  /* Icons are the exact SVG exports from the Figma node. Assigned only via these
     hardcoded constants - no caller string ever reaches innerHTML. */
  var ICON_VALID =
    '<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M15.8333 4.75L7.125 13.4583L3.16667 9.5" stroke="#16A34A" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var ICON_INVALID =
    '<svg width="15.7542" height="15.7542" viewBox="0 0 15.7542 15.7542" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M7.87708 15.0021C11.8121 15.0021 15.0021 11.8121 15.0021 7.87708C15.0021 3.94205 11.8121 0.752083 7.87708 0.752083C3.94205 0.752083 0.752083 3.94205 0.752083 7.87708C0.752083 11.8121 3.94205 15.0021 7.87708 15.0021Z" stroke="#DB3B3B" stroke-width="1.50417" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M7.87708 4.31458V8.27292" stroke="#DB3B3B" stroke-width="1.50417" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M7.87708 11.0438V11.1388" stroke="#DB3B3B" stroke-width="1.50417" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var VALID_TEXT = 'Looks good.';

  // Types that are not text entry, or that we must not touch.
  var SKIP_TYPES = ['hidden', 'submit', 'button', 'reset', 'image', 'file', 'checkbox', 'radio'];

  // Only single-line text inputs get the trailing glyph. A <select> already has a
  // chevron and a <textarea> has no vertical centre worth aligning to, so adding an
  // icon to either collides with the existing design.
  var ICON_TYPES = ['text', 'email', 'tel', 'url', 'password', 'search', ''];

  function isCandidate(el) {
    if (el.hasAttribute('data-fs-skip')) return false;
    if (el.disabled || el.readOnly) return false;
    var tag = el.tagName.toLowerCase();
    if (tag === 'select' || tag === 'textarea') return true;
    if (tag !== 'input') return false;
    if (SKIP_TYPES.indexOf((el.type || '').toLowerCase()) !== -1) return false;
    // Honeypot: off-tab, visually hidden, only bots fill it. Never flag it.
    if (el.getAttribute('tabindex') === '-1') return false;
    return true;
  }

  /* Stricter than the browser where the browser is too generous.

     HTML5 accepts "priya.mehta@gmail" as a valid email - no TLD required. The Figma
     frame shows exactly that string in the ERROR state, so checkValidity() alone
     cannot produce the designed behaviour. This is the same regex the promo popup
     and PromoSubscribe.gs already use, so a lead that passes here also passes
     server-side instead of being accepted by the form and rejected later.

     type="tel" has no native validation at all, so "abc" would sail through a
     required phone field. Digits are counted after stripping the punctuation people
     actually type; the 7-15 range covers Indian mobiles/landlines with or without
     the +91, and international numbers, without hard-coding a country. */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* Phone: an optional leading +, then digits and the separators people actually
     type. Anything else - a letter above all - is rejected outright.
     Counting digits alone is NOT enough: "9026767619wwwww" has ten digits and would
     otherwise pass. That was a real bug. */
  var PHONE_RE = /^\+?[0-9()\s-]+$/;

  // Anything outside this set in a tel field is an error the visitor is told about,
  // rather than something quietly deleted for them.
  var PHONE_ILLEGAL = /[^0-9+()\s-]/;

  function isNameField(el) {
    // Only fields that really are names, not every text input.
    return el.type === 'text' && /name/i.test(el.getAttribute('name') || '');
  }

  function extraInvalid(el) {
    var v = String(el.value || '').trim();
    if (v === '') return false; // emptiness is `required`'s business, not ours

    if (el.type === 'email' && !EMAIL_RE.test(v)) return 'email';

    if (el.type === 'tel') {
      if (!PHONE_RE.test(v)) return 'tel-chars';
      /* Country-code phone fields (phone-field.js) check the exact length for the
         chosen country via setCustomValidity - the generic 7-15 rule would wrongly
         reject short national numbers, so leave the length to it. */
      if (el.hasAttribute('data-pf-number')) return false;
      var digits = v.replace(/[^0-9]/g, '');
      if (digits.length < 7 || digits.length > 15) return 'tel-length';
    }

    if (isNameField(el)) {
      // "123456" and "@@@" are not names. Require a letter and a bit of length.
      if (v.length < 2) return 'name-short';
      if (!/[a-zÀ-ɏ]/i.test(v)) return 'name-letters';
    }

    return false;
  }

  /* A letter in a phone field is reported, not deleted.

     Silently stripping the character is easy to misread as the keyboard being
     broken, and it hides the mistake when the text was pasted. Showing "Numbers
     only" tells the visitor what's wrong and lets them fix it themselves. */
  function hasIllegalTelChar(el) {
    return PHONE_ILLEGAL.test(String(el.value || ''));
  }

  function errorFor(el) {
    var custom = el.getAttribute('data-fs-error');
    if (custom) return custom;
    var v = el.validity;
    if (v.valueMissing) return 'This field is required.';
    var extra = extraInvalid(el);
    if (extra === 'email') return 'Please enter a valid email address.';
    if (extra === 'tel-chars') return 'Numbers only - please remove any letters.';
    if (extra === 'tel-length') return 'Please enter a valid phone number.';
    if (extra === 'name-short') return 'Please enter your full name.';
    if (extra === 'name-letters') return 'Please enter a valid name.';
    // phone-field.js sets a message naming the expected digit count for the country.
    if (v.customError && el.validationMessage) return el.validationMessage;
    if (v.typeMismatch && el.type === 'email') return 'Please enter a valid email address.';
    if (v.typeMismatch && el.type === 'url') return 'Please enter a valid web address.';
    if (el.type === 'tel') return 'Please enter a valid phone number.';
    if (v.patternMismatch) return 'Please check this field.';
    if (v.tooShort || v.tooLong) return 'Please check the length of this entry.';
    // Anything left over: the browser's own wording beats an invented one.
    return el.validationMessage || 'Please check this field.';
  }

  function setup(form) {
    if (form.dataset.fsBound === '1') return;
    form.dataset.fsBound = '1';

    var shellSel = form.getAttribute('data-fs-shell') || '';
    var fields = [];

    Array.prototype.forEach.call(form.querySelectorAll('input, select, textarea'), function (el) {
      if (!isCandidate(el)) return;

      /* Resolve the shell: the element carrying the border. With data-fs-shell the
         input sits inside a bordered wrapper (style-consult); without it the input
         is the bordered thing itself (appointment, contact). */
      var shell = null;
      if (shellSel) {
        try { shell = el.closest(shellSel); } catch (e) { shell = null; }
      }
      var wrapped = false;

      if (!shell || shell === el) {
        shell = el;
        wrapped = true;
      }

      var tag = el.tagName.toLowerCase();
      var wantsIcon = tag === 'input' && ICON_TYPES.indexOf((el.type || '').toLowerCase()) !== -1;

      var host; // element that carries the state class AND hosts the icon
      if (wrapped && wantsIcon) {
        /* The shell is the input, which cannot contain children - give it a
           positioned wrapper so the glyph can overlay its right edge. display:block
           and width:100%, so a column-flex field layout is unaffected. */
        var wrap = document.createElement('span');
        wrap.className = 'fs-wrap fs-host';
        el.parentNode.insertBefore(wrap, el);
        wrap.appendChild(el);
        host = wrap;
      } else {
        host = shell;
        host.classList.add('fs-host');
      }

      if (wantsIcon) {
        var ok = document.createElement('span');
        ok.className = 'fs-icon fs-icon--valid';
        ok.innerHTML = ICON_VALID;
        var bad = document.createElement('span');
        bad.className = 'fs-icon fs-icon--invalid';
        bad.innerHTML = ICON_INVALID;
        host.appendChild(ok);
        host.appendChild(bad);
      }

      /* Helper text goes after the outermost box, so it sits below the field
         rather than inside a bordered wrapper. */
      var anchor = wrapped ? (host === el ? el : host) : shell;
      var msg = document.createElement('p');
      msg.className = 'fs-msg';
      // Announce changes without stealing focus.
      msg.setAttribute('aria-live', 'polite');

      if (!wrapped && shell.parentNode) {
        /* Wrapper-shell forms (style-consult) put the bordered box straight into the
           layout container - and that container may be a flex ROW: .sc__row pairs
           email and phone side by side. Dropping a bare <p> next to the box makes it
           a THIRD row item, which steals width and squashes both fields. So the box
           and its message go into a column group together, and the group takes over
           the row slot. Input-shell forms don't need this: their message already
           lands inside the column .sdf__field / .cf__field. */
        var group = document.createElement('div');
        group.className = 'fs-group';
        shell.parentNode.insertBefore(group, shell);
        group.appendChild(shell);
        group.appendChild(msg);
      } else if (anchor.parentNode) {
        anchor.parentNode.insertBefore(msg, anchor.nextSibling);
      }

      var rec = {
        el: el,
        shell: shell,
        host: host,
        msg: msg,
        touched: false
      };
      fields.push(rec);

      el.addEventListener('focus', function () {
        rec.shell.classList.add('is-fs-focus');
        if (rec.host !== rec.shell) rec.host.classList.add('is-fs-focus');
      });

      el.addEventListener('blur', function () {
        rec.shell.classList.remove('is-fs-focus');
        if (rec.host !== rec.shell) rec.host.classList.remove('is-fs-focus');
        rec.touched = true;
        evaluate(rec);
      });

      if (el.type === 'tel') {
        // Numeric keypad on mobile. Set here so the three sections don't each need
        // the attribute added by hand.
        if (!el.getAttribute('inputmode')) el.setAttribute('inputmode', 'tel');

        /* Typing a letter must complain straight away, without waiting for the
           field to be blurred first. Registered BEFORE the generic `live` listener
           below, so marking the field touched here means `live` evaluates it on the
           very same keystroke. */
        el.addEventListener('input', function () {
          if (hasIllegalTelChar(el)) rec.touched = true;
        });
        el.addEventListener('paste', function () {
          // paste fires before the value lands - check on the next tick.
          setTimeout(function () {
            if (hasIllegalTelChar(el)) rec.touched = true;
            if (rec.touched) evaluate(rec);
          }, 0);
        });
      }

      // Live re-check, but only once they've already left the field once.
      var live = function () { if (rec.touched) evaluate(rec); };
      el.addEventListener('input', live);
      el.addEventListener('change', live);
    });

    function evaluate(rec) {
      var el = rec.el;
      var empty = String(el.value || '').trim() === '';
      var valid = el.checkValidity() && !extraInvalid(el);

      clear(rec);

      // An optional field left blank is neither right nor wrong - say nothing.
      if (empty && !el.required) return valid;

      if (valid) {
        mark(rec, 'valid', el.getAttribute('data-fs-valid') || VALID_TEXT);
      } else {
        mark(rec, 'invalid', errorFor(el));
      }
      return valid;
    }

    function mark(rec, state, text) {
      var cls = 'is-fs-' + state;
      rec.shell.classList.add(cls);
      if (rec.host !== rec.shell) rec.host.classList.add(cls);
      rec.msg.textContent = text;
      rec.msg.className = 'fs-msg is-' + state;
      rec.el.setAttribute('aria-invalid', state === 'invalid' ? 'true' : 'false');
    }

    function clear(rec) {
      rec.shell.classList.remove('is-fs-valid', 'is-fs-invalid');
      if (rec.host !== rec.shell) rec.host.classList.remove('is-fs-valid', 'is-fs-invalid');
      rec.msg.textContent = '';
      rec.msg.className = 'fs-msg';
      rec.el.removeAttribute('aria-invalid');
    }

    form.fieldState = {
      /* Touch and check everything - call this on submit so untouched empty
         required fields light up too. Returns true when the whole form is valid,
         and focuses the first bad field. */
      validateAll: function () {
        var firstBad = null;
        fields.forEach(function (rec) {
          rec.touched = true;
          if (!evaluate(rec) && !firstBad) firstBad = rec;
        });
        if (firstBad) {
          try { firstBad.el.focus({ preventScroll: false }); } catch (e) { firstBad.el.focus(); }
          return false;
        }

        /* Fields this component does NOT manage still carry `required` - checkboxes
           above all. contact-us has an "I'm human" box, and because each section now
           gates on validateAll() instead of checkValidity(), skipping this check
           would let the form submit with it unticked. Inline states can't represent
           a checkbox sensibly, so hand those back to the browser. */
        if (!form.checkValidity()) {
          if (form.reportValidity) form.reportValidity();
          return false;
        }
        return true;
      },
      reset: function () {
        fields.forEach(function (rec) { rec.touched = false; clear(rec); });
      },
      fields: fields
    };
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('form[data-field-state]'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  window.FieldState = { init: init };
})();
